<template>
	<section class="relative flex">
		<!-- Meta tags -->
		<Title>{{ $t("user.notifications.jobsShort") }}</Title>
		<Meta name="description" :content="$t('user.notifications.jobsCta')" />
		<Meta property="og:title" :content="$t('user.notifications.jobsShort')" />
		<Meta property="og:description" :content="$t('user.notifications.jobsCta')" />

		<!-- fix Meta tags -->
		<Link rel="canonical" :href="`https://${globalConfig.host}${$route.fullPath}`" />
		<Link v-if="globalConfig.isBlue" rel="icon" type="image/x-icon" href="/blue-favicon.ico?v3" />
		<Link v-else rel="icon" type="image/x-icon" href="/favicon.ico?v3" />
		<Meta property="og:url" :content="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:type" content="website" />
		<Meta
			property="og:locale"
			:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
		/>
		<Meta
			property="og:image"
			content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
		/>
		<Meta property="og:image:type" content="image/jpeg" />
		<Meta property="og:image:width" content="1200" />
		<Meta property="og:image:height" content="628" />

		<ResponsiveContainer class="relative flex flex-col md:flex-row items-center md:items-start min-h-[900px]">
			<div class="w-full sm:w-[640px] md:w-1/2 px-12 py-14 my-14 mx-auto border border-dj-gray-light rounded-big">
				<BaseHeading type="h1" class="leading-3 text-center md:text-left">
					<span class="text-2xl">{{ $t("user.notifications.jobsShort") }}</span>
				</BaseHeading>
				<p class="mb-7">{{ $t("user.notifications.jobsCta") }}</p>

				<BaseTextbox
					v-model="lastName"
					@keydown="clearErrors()"
					name="lastName"
					:label="$t('registration.field.lastName.label')"
					:placeholder="$t('registration.field.lastName.placeholder')"
					:error="
						lastNameError
							? $t(`errors.${lastNameError}`)
							: (serverFieldErrors['lastName'] && serverFieldErrors['lastName'][0]) || ''
					"
					width="100%"
					class="mb-2"
					required
				/>

				<BaseTextbox
					v-model="firstName"
					@keydown="clearErrors()"
					name="firstName"
					:label="$t('registration.field.firstName.label')"
					:placeholder="$t('registration.field.firstName.placeholder')"
					:error="
						firstNameError
							? $t(`errors.${firstNameError}`)
							: (serverFieldErrors['firstName'] && serverFieldErrors['firstName'][0]) || ''
					"
					width="100%"
					class="mb-2"
					required
				/>

				<BaseTextbox
					v-model="email"
					@keydown="clearErrors()"
					type="email"
					name="email"
					:label="$t('login.field.email.label')"
					:placeholder="$t('login.field.email.placeholder')"
					:error="
						emailError
							? $t(`errors.${emailError}`)
							: (serverFieldErrors['email'] && serverFieldErrors['email'][0]) || ''
					"
					width="100%"
					class="mb-2"
					required
				/>

				<div class="relative w-full pb-6" @click="clearErrors()">
					<SearchbarJobsDetailedLevel
						class="border rounded-normal border-dj-gray-light px-dj-30 py-4"
						:class="{
							'border-dj-red hover:border-dj-red focus-within:border-dj-red focus-within:hover:border-dj-red':
								levelError.length > 0 || serverFieldErrors['job_levels'],
						}"
						v-model="level"
						:label="$t('registration.field.whichLevel')"
						:placeholder="$t('registration.field.whichLevelSelect')"
						:profileField="true"
					/>
					<div class="absolute bottom ml-dj-30 mt-1 font-medium">
						<p v-if="levelError.length > 0" class="text-dj-red text-xs">{{ $t(`errors.${levelError}`) }}</p>
						<p v-else-if="serverFieldErrors['job_levels']" class="text-dj-red text-xs">
							{{ serverFieldErrors["job_levels"][0] }}
						</p>
					</div>
				</div>

				<div class="relative w-full pb-6" @click="clearErrors()">
					<SearchbarJobsCategory
						class="border rounded-normal border-dj-gray-light px-dj-30 py-4"
						:class="{
							'border-dj-red hover:border-dj-red focus-within:border-dj-red focus-within:hover:border-dj-red':
								categoriesError.length > 0 || serverFieldErrors['job_categories'],
						}"
						v-model="categories"
						:label="$t('registration.field.whichJob')"
						:placeholder="$t('registration.field.whichJobSelect')"
						:profileField="true"
					/>
					<div class="absolute bottom ml-dj-30 mt-1 font-medium">
						<p v-if="categoriesError.length > 0" class="text-dj-red text-xs">
							{{ $t(`errors.${categoriesError}`) }}
						</p>
						<p v-else-if="serverFieldErrors['job_categories']" class="text-dj-red text-xs">
							{{ serverFieldErrors["job_categories"][0] }}
						</p>
					</div>
				</div>

				<BaseCheckbox v-model="agreeLegalDocs" class="base-checkbox-legaldocs" @click="clearErrors()">
					<span
						class="text-sm"
						v-html="
							$t(`registration.field.agreeLegalDocs`, {
								urlUserAgreement: getDocumentUrl(LegalDocType.ASZF, region, $i18n.locale),
								urlPrivacyPolicy: getDocumentUrl(LegalDocType.PRIVACY, region, $i18n.locale),
							})
						"
					></span>
				</BaseCheckbox>

				<p v-if="agreeLegalDocsError.length > 0" class="text-dj-red text-xs font-medium mt-1 mb-4">
					{{ $t(`errors.${agreeLegalDocsError}`) }}
				</p>
				<p v-else-if="serverFieldErrors['agree_legal_docs']" class="text-dj-red text-xs font-medium mt-1 mb-2">
					{{ serverFieldErrors["agree_legal_docs"][0] }}
				</p>

				<BaseButton @click="startRegisterFlow" fullwidth class="mt-12 max-w-dj-xs mx-auto">
					{{ $t("user.notifications.signup") }}
				</BaseButton>

				<p
					v-if="Object.keys(serverFieldErrors).length === 0 && serverErrorMsg && serverErrorMsg.length > 0"
					class="text-dj-red text-sm mt-2"
				>
					{{ serverErrorMsg }}
				</p>

				<client-only>
					<vue-recaptcha
						ref="captchalEl"
						@verify="verifyCaptcha"
						:sitekey="siteKey"
						size="invisible"
						badge="bottomleft"
						class="vue-recaptcha"
					></vue-recaptcha>

					<p v-if="reCaptchaError.length > 0" class="text-dj-red text-xs font-medium mt-1 mb-4">
						{{ $t(`errors.${reCaptchaError}`) }}
					</p>
				</client-only>
			</div>
		</ResponsiveContainer>
	</section>
</template>
<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";

// reCaptcha how to: https://www.youtube.com/watch?v=slQwnKFV3iA
// reCaptcha admin: https://www.google.com/recaptcha/admin/
import { VueRecaptcha } from "vue-recaptcha";
import { getDocumentUrl, LegalDocType } from "~/utils/legalDocs";

export default defineComponent({
	name: "RegistrationPage",
	components: {
		VueRecaptcha,
	},
	setup() {
		const { locale, t } = useI18n();
		const region = ref("HU");

		const emailRegExp = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

		const config = useRuntimeConfig();
		const siteKey = config.public.reCaptcha.siteKey;

		const app = useNuxtApp();
		const gtm = useGTM();

		const apiUrl = ref(globalConfig.API);

		const captchalEl = ref(null);

		const lastName = ref("");
		const firstName = ref("");
		const email = ref("");
		const categories = ref({ categories: [], subcategories: [] });
		const level = ref<number[]>([]);
		const agreeLegalDocs = ref(false);

		const reCaptchaVerified = ref(false);
		config.public.envMode == "development" ? (reCaptchaVerified.value = true) : null;

		const lastNameError = ref("");
		const firstNameError = ref("");
		const emailError = ref("");
		const levelError = ref("");
		const categoriesError = ref("");
		const agreeLegalDocsError = ref("");
		const reCaptchaError = ref("");

		const serverFieldErrors = ref({});
		const serverErrorMsg = ref("");

		function clearErrors() {
			lastNameError.value.length > 0 ? (lastNameError.value = "") : null;
			firstNameError.value.length > 0 ? (firstNameError.value = "") : null;
			emailError.value.length > 0 ? (emailError.value = "") : null;
			levelError.value.length > 0 ? (levelError.value = "") : null;
			categoriesError.value.length > 0 ? (categoriesError.value = "") : null;
			agreeLegalDocsError.value.length > 0 ? (agreeLegalDocsError.value = "") : null;

			reCaptchaVerified.value == true ? (reCaptchaError.value = "") : null;

			Object.keys(serverFieldErrors.value).length ? (serverFieldErrors.value = {}) : null;
			serverErrorMsg.value.length > 0 ? (serverErrorMsg.value = "") : null;
		}

		function startRegisterFlow() {
			// reCaptcha
			captchalEl.value.execute();

			// Validations
			lastName.value.length === 0 ? (lastNameError.value = "requiredLastName") : null;
			firstName.value.length === 0 ? (firstNameError.value = "requiredFirstName") : null;
			!emailRegExp.test(email.value) ? (emailError.value = "invalidEmail") : null;
			email.value.length === 0 ? (emailError.value = "requiredEmail") : emailError.value;

			level.value.length === 0 ? (levelError.value = "requiredLevel") : null;
			categories.value.categories.length === 0 ? (categoriesError.value = "requiredCategory") : null;

			agreeLegalDocs.value === false ? (agreeLegalDocsError.value = "requiredField") : null;

			reCaptchaVerified.value === false ? (reCaptchaError.value = "generic") : null;

			if (
				(
					lastNameError.value +
					firstNameError.value +
					emailError.value +
					levelError.value +
					categoriesError.value +
					agreeLegalDocsError.value +
					reCaptchaError.value
				).length < 1
			) {
				registerUser();
			}
		}

		function registerUser() {
			const registrationData = {
				locale: locale.value,
				agree_legal_docs: agreeLegalDocs.value,
				email: email.value,
				preferred_lang: locale.value,
				last_name: lastName.value,
				first_name: firstName.value,
				region: region.value.toLowerCase(),
				job_categories: categories.value.categories.concat(categories.value.subcategories),
				job_levels: level.value,
				job_notification_subscribe: true,
			};

			useAuth
				.register(registrationData)
				.then(async () => {
					try {
						app.$openToast({
							message: t("user.notifications.signupsuccessful"),
							type: "success",
							description: "",
						});
						lastName.value = "";
						firstName.value = "";
						email.value = "";
						categories.value = { categories: [], subcategories: [] };
						level.value = [];
						window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
					} catch (error) {
						console.error(error);
					}

					// GTM tracking
					gtm.trackEvent({ event: "b2c-jobnotification-signup" });
				})
				.catch(async (error) => {
					if (error.response && error.response._data) {
						serverFieldErrors.value = error.response._data.data.errors || {};
					}
					if (error.response && error.response._data && error.response._data.message) {
						serverErrorMsg.value = error.statusText || error.response._data.message || "";
					}
				});
		}

		function verifyCaptcha(response) {
			if (response.length > 0) {
				reCaptchaVerified.value = true;
			}
		}

		onMounted(() => {
			region.value = useRegion.getCurrentRegion().code;

			setTimeout(() => {
				captchalEl.value.execute();
			}, 1000);
		});

		return {
			region,
			siteKey,
			captchalEl,
			apiUrl,
			lastName,
			firstName,
			email,
			level,
			categories,
			agreeLegalDocs,
			lastNameError,
			firstNameError,
			emailError,
			levelError,
			categoriesError,
			agreeLegalDocsError,
			reCaptchaError,
			serverErrorMsg,
			serverFieldErrors,
			startRegisterFlow,
			clearErrors,
			verifyCaptcha,
			getDocumentUrl,
			LegalDocType,
		};
	},
});
</script>
<style lang="scss">
.base-checkbox-legaldocs {
	a {
		color: #1e64e1;
	}
}

.vue-recaptcha {
	.grecaptcha-badge {
		z-index: 5;
	}
}
</style>
